import type { Action } from 'vuex'
import type { State } from './state'
import type {
  CartDocument,
  CartRecipient,
  Client,
  ConfirmPageInitErrorDatas,
  CorporateInfo,
  DeleteMultiCartDocument,
  DeleteMultiCartRecipient,
  RecipientClientDetail,
  RecipientClientList,
  RecipientMasterOptions,
  RecipientSelectCheckErrors,
  RecipientUserList,
} from '@/api/multi-send-cart/model/multiSendCartModel'
import type { FolderTreeItem } from '@/api/model/folderModel'

import actionTypes from './actionTypes'
import { formatFolderList } from '@/utils'
import { getMultiCartsCount, getRecipientFolderList, getRecipientMasterOptions } from '@/api/multi-send-cart/multi-send-cart'
import usePageData, { Feature, PageCode } from '@/hooks/core/usePageData'

const { savePageData } = usePageData()

const actions: { [actionTypes: string]: Action<State, any> } = {
  /** @description 送信カート件数を設定する */
  async [actionTypes.SET_MULTI_CARTS_COUNT]({ commit }): Promise<void> {
    const count = await getMultiCartsCount()
    savePageData(PageCode.SIDEBAR, Feature.INITIALIZATION, { multiCartsCount: count })
    commit(actionTypes.SET_MULTI_CARTS_COUNT, count)
  },

  /** @description 送信カート 送信対象テーブルデータを設定する */
  [actionTypes.SET_SEND_CART_DOCUMENT]({ commit }, data: CartDocument): void {
    commit(actionTypes.SET_SEND_CART_DOCUMENT, data)
  },

  /** @description 送信対象 削除確認ダイヤログ画面表示フラグを設定する */
  [actionTypes.SET_MODAL_DELETE_SEND_DOCUMENT_ACTIVED](
    { commit },
    status: boolean
  ): void {
    commit(actionTypes.SET_MODAL_DELETE_SEND_DOCUMENT_ACTIVED, status)
  },

  /** @description 送信先 削除確認ダイヤログ画面表示フラグを設定する */
  [actionTypes.SET_MODAL_DELETE_SEND_RECIPIENT_ACTIVED](
    { commit },
    status: boolean
  ): void {
    commit(actionTypes.SET_MODAL_DELETE_SEND_RECIPIENT_ACTIVED, status)
  },

  /** @description 送信カートの送信メッセージを設定する */
  [actionTypes.SET_SEND_CART_MESSAGE]({ commit }, text: string): void {
    commit(actionTypes.SET_SEND_CART_MESSAGE, text)
  },

  /** @description 送信カート 送信先テーブルデータを設定する */
  [actionTypes.SET_SEND_CART_RECIPIENT]({ commit }, data: CartRecipient): void {
    commit(actionTypes.SET_SEND_CART_RECIPIENT, data)
  },

  /** @description 送信カート 送信先選択画面表示フラグを設定する */
  [actionTypes.SET_MODAL_RECIPIENT_SELECT_ACTIVED]({ commit }, status: boolean): void {
    commit(actionTypes.SET_MODAL_RECIPIENT_SELECT_ACTIVED, status)
  },

  /** @description 送信カート  全てのデータをクリアする確認ダイヤログ画面表示フラグを設定する */
  [actionTypes.SET_MODAL_CLEAR_SEND_CART_ACTIVED]({ commit }, status: boolean): void {
    commit(actionTypes.SET_MODAL_CLEAR_SEND_CART_ACTIVED, status)
  },

  /** @description 送信カート  削除する送信先を設定する */
  [actionTypes.SET_DELETE_SEND_CART_RECIPIENT]({ commit }, target: DeleteMultiCartRecipient): void {
    commit(actionTypes.SET_DELETE_SEND_CART_RECIPIENT, target)
  },

  /** @description 送信カート  削除する送信対象を設定する */
  [actionTypes.SET_DELETE_SEND_CART_DOCUMENT]({ commit }, target: DeleteMultiCartDocument): void {
    commit(actionTypes.SET_DELETE_SEND_CART_DOCUMENT, target)
  },

  /** @description 送信先選択画面　送信先マスタoptionsを設定する  */
  [actionTypes.SET_RECIPIENT_MASTER_OPTIONS](
    { commit },
    options: RecipientMasterOptions
  ): void {
    commit(actionTypes.SET_RECIPIENT_MASTER_OPTIONS, options)
  },

  /** @description 送信先選択画面　送信先マスタoptionsデータ初期化 */
  [actionTypes.INIT_RECIPIENT_MASTER_OPTIONS]: async ({ commit }): Promise<void> => {
    const result: RecipientMasterOptions | undefined = await getRecipientMasterOptions()

    if (!result) {
      return
    } else {
      commit(actionTypes.SET_RECIPIENT_MASTER_OPTIONS, result)
    }
  },

  /** @description 送信先選択画面　送信先マスタoptionsデータをクリア */
  [actionTypes.CLEAR_RECIPIENT_MASTER_OPTIONS]: ({ commit }): void => {
    commit(actionTypes.SET_RECIPIENT_MASTER_OPTIONS, [])
  },

  /** @description 送信先選択画面　取引先選択リスト バックアップデータを設定する  */
  [actionTypes.SET_RECIPIENT_CLIENT_LIST]: (
    { commit },
    list: RecipientClientList
  ): void => {
    commit(actionTypes.SET_RECIPIENT_CLIENT_LIST, list)
  },

  /** @description 送信先選択画面　取引先選択リスト 表示用データを設定する  */
  [actionTypes.SET_DISPLAY_CLIENT_LIST]: (
    { commit },
    list: RecipientClientList
  ): void => {
    commit(actionTypes.SET_DISPLAY_CLIENT_LIST, list)
  },

  /** @description 送信先選択画面　選択した取引先情報を設定する  */
  [actionTypes.SET_SELECTED_CLIENT]: (
    { commit },
    selectedClient: Client
  ): void => {
    commit(actionTypes.SET_SELECTED_CLIENT, selectedClient)
  },

  /** @description 送信先選択画面　法人情報を設定する  */
  [actionTypes.SET_RECIPIENT_CORPORATE_INFO]: (
    { commit },
    corporateInfo: CorporateInfo
  ): void => {
    commit(actionTypes.SET_RECIPIENT_CORPORATE_INFO, corporateInfo)
  },

  /** @description 送信先選択画面　担当者情報を設定する  */
  [actionTypes.SET_RECIPIENT_USER_LIST]: (
    { commit },
    recipientUserList: RecipientUserList
  ): void => {
    commit(actionTypes.SET_RECIPIENT_USER_LIST, recipientUserList)
  },

  /** @description 送信先選択画面　担当者情報をクリアする  */
  [actionTypes.CLEAR_RECIPIENT_USER_LIST]: (
    { commit },
  ): void => {
    commit(actionTypes.CLEAR_RECIPIENT_USER_LIST)
  },

  /** @description 送信先選択画面　選択した担当者を設定する  */
  [actionTypes.SET_SELECTED_USER]: (
    { commit },
    selectedRecipientUser: RecipientClientDetail
  ): void => {
    commit(actionTypes.SET_SELECTED_USER, selectedRecipientUser)
  },

  /** @description 送信先選択画面　送信先フォルダ情報を設定する  */
  [actionTypes.SET_RECIPIENT_FOLDER_TREE_LIST]: async (
    { commit },
    corporateId: number
  ): Promise<void> => {
    const result = await getRecipientFolderList(corporateId)
    const folderTreeList = formatFolderList(result)
    commit(actionTypes.SET_RECIPIENT_FOLDER_TREE_LIST, folderTreeList)
  },

  /** @description 送信先選択画面　送信先フォルダ情報をクリアする  */
  [actionTypes.CLEAR_RECIPIENT_FOLDER_TREE_LIST]: async (
    { commit },
  ): Promise<void> => {
    commit(actionTypes.CLEAR_RECIPIENT_FOLDER_TREE_LIST)
  },

  /** @description 送信先選択画面　選択した送信先フォルダを設定する  */
  [actionTypes.SET_SELECTED_FOLDER_TREE]: (
    { commit }, selectedFolderTree: FolderTreeItem
  ): void => {
    commit(actionTypes.SET_SELECTED_FOLDER_TREE, selectedFolderTree)
  },

  /** @description 送信先選択画面 「追加する」ボタン押下後のチェックエラー */
  [actionTypes.SET_CHECK_ERRORS]: (
    { commit }, setCheckErrors: RecipientSelectCheckErrors
  ): void => {
    commit(actionTypes.SET_CHECK_ERRORS, setCheckErrors)
  },

  /** @description 送信完了画面　表示フラグを設定する */
  [actionTypes.SET_MODAL_SEND_FINISHED_ACTIVED](
    { commit },
    status: boolean
  ): void {
    commit(actionTypes.SET_MODAL_SEND_FINISHED_ACTIVED, status)
  },

  /** @description 送信カート確認画面　送信先チェックのエラーメーセージとレベルを設定する */
  [actionTypes.SET_CONFIRM_PAGE_INIT_ERROR_DATAS](
    { commit },
    datas: ConfirmPageInitErrorDatas
  ): void {
    commit(actionTypes.SET_CONFIRM_PAGE_INIT_ERROR_DATAS, datas)
  },

  [actionTypes.SET_SORT_COLUMN_EXIST](
    { commit },
    status: boolean
  ): void {
    commit(actionTypes.SET_SORT_COLUMN_EXIST, status)
  },

  [actionTypes.SET_SORT_COLUMN_RECIPIENT_EXIST](
    { commit },
    status: boolean
  ): void {
    commit(actionTypes.SET_SORT_COLUMN_RECIPIENT_EXIST, status)
  },
}

export default actions
